import request from '@/utils/request'
import type { AxiosResponse } from 'axios'
import type { MailboxConnection, MailScope, MailVisibility } from '@/models/MailboxConnection'
import { extractApiErrors } from '@/utils/apiErrors'

export interface ImapCredentials {
  host: string
  port: number
  encryption: 'ssl' | 'tls' | 'starttls' | 'none'
  username: string
  password: string
  validate_cert: boolean
}

export interface CreateImapConnectionBody {
  provider: 'imap'
  scope: MailScope
  visibility: MailVisibility
  space_id?: string | null
  display_name?: string
  credentials: ImapCredentials
}

export interface UpdateMailboxConnectionBody {
  display_name?: string
  visibility?: MailVisibility
  space_id?: string | null
  credentials?: Partial<ImapCredentials>
}

export interface StartOAuthBody {
  scope: MailScope
  shared_address?: string
  visibility: MailVisibility
  space_id?: string | null
  connection_id?: string
}

/** GET /api/v2/mailbox-connections */
export function getMailboxConnections(scope?: MailScope): Promise<AxiosResponse<{ data: MailboxConnection[] }>> {
  return request({ url: '/api/v2/mailbox-connections', method: 'get', params: scope ? { scope } : undefined })
}

/** GET /api/v2/mailbox-connections/{id} */
export function getMailboxConnection(id: string): Promise<AxiosResponse<{ data: MailboxConnection }>> {
  return request({ url: '/api/v2/mailbox-connections/' + id, method: 'get' })
}

/** POST /api/v2/mailbox-connections */
export function createImapConnection(data: CreateImapConnectionBody): Promise<AxiosResponse<{ data: MailboxConnection }>> {
  return request({ url: '/api/v2/mailbox-connections', method: 'post', data })
}

/** PATCH /api/v2/mailbox-connections/{id} */
export function updateMailboxConnection(
  id: string,
  data: UpdateMailboxConnectionBody,
): Promise<AxiosResponse<{ data: MailboxConnection }>> {
  return request({ url: '/api/v2/mailbox-connections/' + id, method: 'patch', data })
}

/** DELETE /api/v2/mailbox-connections/{id} */
export function deleteMailboxConnection(id: string): Promise<AxiosResponse<void>> {
  return request({ url: '/api/v2/mailbox-connections/' + id, method: 'delete' })
}

/** POST /api/v2/mailbox-connections/{id}/test */
export function testMailboxConnection(id: string): Promise<AxiosResponse<{ ok: boolean; message: string | null }>> {
  return request({ url: '/api/v2/mailbox-connections/' + id + '/test', method: 'post' })
}

/** POST /api/v2/mailbox-connections/{id}/sync (202; 429 SYNC_RATE_LIMITED) */
export function syncMailboxConnection(id: string): Promise<AxiosResponse<{ queued: boolean }>> {
  return request({ url: '/api/v2/mailbox-connections/' + id + '/sync', method: 'post' })
}

/** Result of a dry run: what a folder selection would change. */
export interface FolderSelectionPreview {
  /** Mails that would be removed from Centrics entirely (their only source is a removed folder). */
  to_delete: number
  /** Mails of removed folders that stay because they are linked to a task or protocol; absent in older responses. */
  retained?: number
  added: string[]
  removed: string[]
}

export interface FolderSelectionResult {
  data: MailboxConnection
  to_delete: number
}

/**
 * PUT /api/v2/mailbox-connections/{id}/folders
 * Sets the selected folders (the complete list). With `dryRun` nothing changes and the server
 * only reports the effect. Errors: 422 FOLDER_NOT_SELECTABLE.
 */
export function updateMailboxFolders<DryRun extends boolean = false>(
  id: string,
  selected: string[],
  dryRun: DryRun = false as DryRun,
): Promise<AxiosResponse<DryRun extends true ? { data: FolderSelectionPreview } : FolderSelectionResult>> {
  return request({ url: '/api/v2/mailbox-connections/' + id + '/folders', method: 'put', data: { selected, dry_run: dryRun } })
}

/** POST /api/v2/mailbox-connections/oauth/{provider}/start */
export function startOAuth(
  provider: 'microsoft' | 'google',
  data: StartOAuthBody,
): Promise<AxiosResponse<{ authorize_url: string }>> {
  return request({ url: '/api/v2/mailbox-connections/oauth/' + provider + '/start', method: 'post', data })
}

/**
 * POST /api/v2/mailbox-connections/oauth/complete
 * Completes an OAuth connection with the one-time handle from the callback redirect; the request is authenticated
 * and only succeeds for the user who started the flow. Errors: OAUTH_HANDLE_INVALID, MAILBOX_MISMATCH,
 * DUPLICATE_CONNECTION, MAIL_MODULE_DISABLED.
 */
export function completeOAuth(handle: string): Promise<AxiosResponse<{ data: MailboxConnection }>> {
  return request({ url: '/api/v2/mailbox-connections/oauth/complete', method: 'post', data: { handle } })
}

/**
 * Machine error code of a failed mail request, e.g. `SYNC_RATE_LIMITED`. A code may carry a provider
 * message after `: ` (`IMAP_CONNECTION_FAILED: <message>`); only the code part is returned.
 */
export function mailErrorCode(error: unknown): string | null {
  const { code, messages } = extractApiErrors(error)
  const pattern = /^([A-Z][A-Z0-9_]+)(?::|$)/
  for (const candidate of [code, ...messages]) {
    const match = candidate ? pattern.exec(candidate) : null
    if (match && !match[1].startsWith('HTTP_')) return match[1]
  }
  return null
}
